import React, { Component } from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { uniq, isEmpty } from 'lodash';
import { openModal } from 'actions/modalActions';
import { importGroups } from 'actions/groupsActions';
import { genGroupsValidationData } from 'validation/importGroups';
import Select from 'components/shared/Select';

class ImportGroupsModal extends Component {
  constructor(props) {
    super(props);
    this.onSheetChange = this.onSheetChange.bind(this);
    this.onMapChange = this.onMapChange.bind(this);
    let groupsColumns = ['groups', 'code', 'parentCode', 'number'];
    this.state = {
      // Initially select first sheet
      sheetIndex: 0,
      // Set initial mapping of sheet columns to account, for each sheet
      // sheetMap[sheetIndex][accountColumn] = selectedOptionIndex
      sheetMap: this.props.data.wsnames.reduce((ob1, wsname, wsi) => {
        ob1[wsi] = groupsColumns.reduce((ob2, ac, i) => {
          const headerRow = this.props.data.wsdata[wsi][0];
          // if there are no enough columns in header row leave unmapped
          if (headerRow) {
            ob2[ac] = i < headerRow.length ? i : -1;
          }
          return ob2;
        }, {});
        return ob1;
      }, {}),
      groupsColumns,
    };
  }

  hasAllColumns() {
    const sheetMap = this.state.sheetMap[this.state.sheetIndex];
    return !Object.keys(sheetMap).some((k) => sheetMap[k] < 0);
  }

  hasInputFileError({ data }) {
    return !data.every(isEmpty);
  }

  generateErrorMessage({ data, messageText }) {
    if (data.length) {
      return (
        <React.Fragment key={messageText}>
          <p>{`${messageText} (total: ${data.length}):`}</p>
          <p>
            {uniq(data)
              .filter((el) => el !== undefined)
              .toString()}
          </p>
        </React.Fragment>
      );
    }
  }

  /*
   * For currently selected mapping checks and returns array of messages
   *    how many rows will be used,
   *    if there is no mapping for groups name, code or parent code
   *    checks for duplicate code, number in input file
   *    checks for rows with same parentCode and code
   */
  updateMessages() {
    const modalMessages = [];

    const hasAllColumns = this.hasAllColumns();
    if (!hasAllColumns) {
      modalMessages.push(
        <p key="selectAll">'You need to select all columns.'</p>
      );
      return modalMessages;
    }

    const sheetData = this.props.data.wsdata[this.state.sheetIndex];
    const sheetMap = this.state.sheetMap[this.state.sheetIndex];

    const {
      groupCodesDuplicates,
      groupNumbersDuplicates,
      sameCodeAndParentCode,
      rowsCount,
    } = genGroupsValidationData({ sheetData, sheetMap });

    modalMessages.push(
      <p key="totalRows">{`Total rows (groups) to be imported: ${rowsCount}`}</p>,
      <p key="notImported">{`Total rows (groups) without group code (will not be imported): ${
        sheetData.length - rowsCount
      }`}</p>
    );

    const hasInputFileError = this.hasInputFileError({
      data: [
        groupCodesDuplicates,
        groupNumbersDuplicates,
        sameCodeAndParentCode,
      ],
    });

    if (hasInputFileError) {
      modalMessages.push(
        <p key="error">
          <strong>Input File Errors:</strong>
        </p>
      );
    }

    // generate error messages
    const codesErrorMessages = this.generateErrorMessage({
      data: groupCodesDuplicates,
      messageText: 'List of duplicate group Codes',
    });
    const numbersErrorMessages = this.generateErrorMessage({
      data: groupNumbersDuplicates,
      messageText: 'List of duplicate group Numbers',
    });
    const sameCodeAndParentCodeErrorMessages = this.generateErrorMessage({
      data: groupCodesDuplicates,
      messageText: 'Rows with the same Code and ParentCode',
    });

    // push error messages
    modalMessages.push(
      codesErrorMessages,
      numbersErrorMessages,
      sameCodeAndParentCodeErrorMessages
    );

    const hasError = hasInputFileError || !hasAllColumns;

    return { modalMessages, hasError };
  }

  onSheetChange(e) {
    this.setState({
      sheetIndex: e.target.selectedIndex,
    });
  }

  onMapChange(e) {
    const { id, value } = e.target;
    this.setState((prevState) => ({
      sheetMap: {
        ...prevState.sheetMap,
        [prevState.sheetIndex]: {
          ...prevState.sheetMap[prevState.sheetIndex],
          [id]: parseInt(value, 10),
        },
      },
    }));
  }

  handleSubmit = (event) => {
    const { openModal, groups } = this.props;
    const groupsLength =
      groups && groups.groupsById ? Object.keys(groups.groupsById).length : 0;

    // There is always at least one group - 'Financial statement'
    if (groupsLength > 1) {
      openModal({
        id: uuid(),
        type: 'confirmation',
        text:
          'Are you sure you want to import groups? Old groups will be deleted.',
        onConfirm: this.importGroupsAction,
      });
    } else {
      this.importGroupsAction();
    }

    event.preventDefault();
  };

  importGroupsAction = () => {
    this.props.importGroups(
      this.props.data.wsdata[this.state.sheetIndex].slice(1),
      this.state.sheetMap[this.state.sheetIndex]
    );
    this.props.onClose();
  };

  render() {
    const worksheets = this.props.data.wsnames.map((name, i) => (
      <option key={i}>{name}</option>
    ));
    const sheetMap = this.state.sheetMap[this.state.sheetIndex];
    const mapSelectors = this.state.groupsColumns.map((groupCol, i) => {
      return (
        <tr key={i}>
          <td>{groupCol}</td>
          <td>
            <Select
              id={groupCol}
              options={this.props.data.wsdata[this.state.sheetIndex][0]}
              selectedIndex={sheetMap[groupCol]}
              onChange={this.onMapChange}
            />
          </td>
        </tr>
      );
    });
    const { modalMessages, hasError } = this.updateMessages();

    return (
      <div className="ImportAccountsModal">
        <h2>Import groups</h2>
        <p>Select sheet:</p>
        <select onChange={this.onSheetChange}>{worksheets}</select>
        <p>Map columns:</p>
        <table>
          <tbody>
            <tr key={-1}>
              <th>Group Field</th>
              <th>Sheet Column</th>
            </tr>
            {mapSelectors}
          </tbody>
        </table>
        <div className="messages">{modalMessages}</div>
        <form onSubmit={this.handleSubmit}>
          <input
            type="submit"
            value="Import"
            className="btn"
            disabled={hasError}
          />
        </form>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    groups: state.groups,
  }),
  {
    openModal,
    importGroups,
  }
)(ImportGroupsModal);
